import CmsBanner from "../../components/Banner/CmsBanner";
import ProfileIcon from "../../assets/images/myaccount/profile-icon.svg";
import BlueBtn from "../../components/button/PrimaryBtn";
import MintGreenBtn from "../../components/button/MintGreenBtn";
import { useRef, useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import GreenBtn from "../../components/button/SecondaryBtn";
import ChangePasswordModal from "../../components/Modal/myaccountmodal/ChangePasswordModal";
import DeleteAccountModal from "../../components/Modal/myaccountmodal/DeleteAccountModal";
import RedBtn from "../../components/button/RedBtn";
import useAppSelector from "../../hooks/useAppSelector";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCharacterListAPI, updateUserProfileAPI } from "../../api/user";
import { checkPhoneExistsUpdateAPI, getCountryListAPI, sendEmailUpdateVerificationAPI } from "../../api/auth";
import showToast from "../../hooks/useToast";
import { REGEX } from "../../utils/constant";
import { setUser } from "../../redux/slices/userSlice";
import { useDispatch } from "react-redux";
import { openModal } from "../../redux/slices/uiSlice";
import { clearPhoneUpdateVerificationData, setEmailVerification, setOtpVerificationData } from "../../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineGlobeAlt } from "react-icons/hi";
import { sendPhoneOtp } from "../../utils/firebaseConfig";
import {
  getPhoneLengthErrorKey,
  getPhoneMaxLength,
  getSendPhoneOtpToastMessage,
  isFirebaseInvalidPhoneError,
  isValidPhoneLength,
  sanitizePhoneInput,
} from "../../utils/phoneValidation";
import { isInappropriateUsername } from "../../utils/usernameValidation";

import { FiCalendar, FiMail } from "react-icons/fi";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// Country code to flag emoji mapping
const COUNTRY_FLAGS: Record<string, string> = {
  "973": "🇧🇭", // Bahrain
  "965": "🇰🇼", // Kuwait
  "968": "🇴🇲", // Oman
  "974": "🇶🇦", // Qatar
  "966": "🇸🇦", // Saudi Arabia
  "971": "🇦🇪", // UAE
};
const inputClasses =
  "2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent";

const labelClasses = "block lg:text-[1vw] text-sm font-bold text-black-300 pt-3  md:pt-4 leading-none";

type CharacterItem = {
  id: string;
  image: string;
  type: string;
  gender: "male" | "female";
};

// Helper function to capitalize first letter of each word
const capitalizeWords = (str: string): string => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function MyAccount() {
  const [activeModal, setActiveModal] = useState(null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isCountryPositionReady, setIsCountryPositionReady] = useState(false);
  const countryButtonRef = useRef<HTMLButtonElement>(null);
  const [isNationalityDropdownOpen, setIsNationalityDropdownOpen] = useState(false);
  const [nationalitySearchQuery, setNationalitySearchQuery] = useState("");
  const [nationalityDropdownPosition, setNationalityDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isNationalityPositionReady, setIsNationalityPositionReady] = useState(false);
  const nationalityButtonRef = useRef<HTMLButtonElement>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const [isDobPickerOpen, setIsDobPickerOpen] = useState(false);
  const [dobDropdownPosition, setDobDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isDobPositionReady, setIsDobPositionReady] = useState(false);
  const dobButtonRef = useRef<HTMLButtonElement>(null);
  const user = useAppSelector((state: any) => state.user.user);
  const phoneUpdateVerification = useAppSelector((state: any) => state.auth.phoneUpdateVerification);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSendingPhoneOtp, setIsSendingPhoneOtp] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState<{ phoneNumber: string; countryCode?: string } | null>(null);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false);
  // Fetch country list from API
  const { data: countryListData, isLoading: isCountryListLoading } = useQuery({
    queryKey: ["countryList"],
    queryFn: getCountryListAPI,
  });
  const { data: characterListData } = useQuery({
    queryKey: ["characterList"],
    queryFn: () => getCharacterListAPI(),
  });

  const countryList = countryListData?.data || [];
  const standingCharacters: CharacterItem[] = useMemo(() => {
    const characters = (characterListData as any)?.data?.characters || [];
    return characters.filter((item: CharacterItem) => item.type === "standing");
  }, [characterListData]);

  // Memoize initial values to recalculate when countryList or user changes
  const initialValues = useMemo(() => {
    // Convert user's address (string in current language) to JSON string for select matching
    const getAddressValue = () => {
      if (!user?.address || countryList.length === 0) return "";

      const userAddress = user.address;

      // If address is already an object, stringify it
      if (typeof userAddress === "object") {
        return JSON.stringify(userAddress);
      }

      // If it's a string, find the matching country from API list
      // The API returns the country name in the current language
      const matchingCountry = countryList.find(
        (country: { country_name_full: { en?: string; ar?: string } }) =>
          country.country_name_full?.en === userAddress || country.country_name_full?.ar === userAddress
      );

      if (matchingCountry) {
        // Return the stringified version from API (ensures exact match with select options)
        return JSON.stringify(matchingCountry.country_name_full);
      }

      // Try parsing as JSON in case it's already stringified
      try {
        JSON.parse(userAddress);
        return userAddress; // It's valid JSON, use as is
      } catch {
        return ""; // Not a valid country
      }
    };

    return {
      full_name: user?.full_name || "",
      dob: user?.dob || "",
      gender: user?.gender || "",
      country_code: user?.country_code || "973", // Default to Bahrain (+973) if not set
      phone_number: user?.phone_number || "",
      email: user?.email || "",
      address: getAddressValue(),
      profile_image: user?.profile_image || null,
    };
  }, [user, countryList, currentLanguage]);

  // Maximum DOB date: user must be at least 13 years old
  const maxDobDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 13);
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  const todayDobDate = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  const validationSchema = Yup.object().shape({
    full_name: Yup.string()
      .trim()
      .required(t("validation.nameRequired"))
      .matches(/^[a-zA-Z\u0600-\u06FF\s]+$/, t("validation.nameValid"))
      .test("not-only-spaces", t("validation.nameSpaces"), (value) => (value ? value.trim().length > 0 : false))
      .test("not-inappropriate", t("validation.nameInappropriate"), (value) => !value || !isInappropriateUsername(value))
      .min(2, t("validation.nameMin"))
      .max(20, t("validation.nameMax")),
    dob: Yup.date()
      .required(t("validation.dobRequired"))
      .typeError(t("validation.dobRequired"))
      .max(todayDobDate, t("validation.dobFuture"))
      .max(maxDobDate, t("validation.dobMinAge")),
    gender: Yup.string().required(t("validation.genderRequired")),
    address: Yup.mixed().nullable().required(t("validation.selectNationality")),
    phone_number: Yup.string()
      .nullable()
      .test("phone-number", t("validation.mobileValid"), function (value) {
        const { createError, path, parent } = this;
        if (!value) return true;
        if (!REGEX.NUMBERS_ONLY.test(value)) {
          return createError({ path, message: t("validation.mobileValid") });
        }
        if (!isValidPhoneLength(value, parent.country_code)) {
          return createError({
            path,
            message: t(`validation.${getPhoneLengthErrorKey(parent.country_code)}`),
          });
        }
        return true;
      }),
    email: Yup.mixed()
      .nullable()
      .test("email", t("validation.emailValid"), function (value) {
        const { createError, path, parent } = this;
        if (!parent.phone_number && !parent.email) {
          return createError({ path, message: t("validation.emailRequired") });
        }
        if (parent.email && !REGEX.EMAIL.test(value as string)) {
          return createError({ path, message: t("validation.emailValid") });
        }
        return true;
      }),
  });

  const { handleSubmit, values, errors, touched, handleChange, setValues, setFieldValue, setFieldError, setFieldTouched, validateForm, handleBlur } = useFormik({
    initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const currentPhoneNumber = values.phone_number || "";
      const currentCountryCode = values.country_code || "973";
      const existingPhoneNumber = user?.phone_number || "";
      const existingCountryCode = user?.country_code || "973";
      const hasPhoneChanged = currentPhoneNumber !== existingPhoneNumber || currentCountryCode !== existingCountryCode;
      const needsPhoneVerification = hasPhoneChanged && !!currentPhoneNumber;
      const isCurrentPhoneVerified =
        !needsPhoneVerification ||
        (verifiedPhone?.phoneNumber === currentPhoneNumber && (verifiedPhone?.countryCode || "") === currentCountryCode);

      if (needsPhoneVerification && !isCurrentPhoneVerified) {
        showToast(t("myAccount.verifyPhoneToSave"), "error");
        return;
      }

      const formData = new FormData();
      formData.append("full_name", values.full_name.trim());
      if (values.phone_number) {
        formData.append("phone_number", values.phone_number);
        if (values.country_code) {
          formData.append("country_code", values.country_code);
        }
      }
      if (values.email) formData.append("email", values.email);
      if (values.dob) formData.append("dob", values.dob);
      if (values.gender) formData.append("gender", values.gender);
      if (values.address) {
        // address is already a JSON string from the select option value
        formData.append("address", values.address);
      }
      // Handle profile_image based on state:
      // 1. New file selected → pass the File
      // 2. Image removed (null) → don't pass the key
      // 3. Unchanged (same as original) → pass the original URL
      if (values.profile_image instanceof File) {
        // New image selected
        formData.append("profile_image", values.profile_image);
      } else if (values.profile_image === null) {
        // Image was removed - don't pass the key
      } else if (values.profile_image) {
        // Unchanged - pass the original URL
        formData.append("profile_image", values.profile_image);
      }
      mutate(formData);
    },
  });
  const filteredStandingCharacters: CharacterItem[] = useMemo(() => {
    if (!values.gender) return [];
    return standingCharacters.filter(
      (item: CharacterItem) => (item.gender || "").toLowerCase() === (values.gender || "").toLowerCase()
    );
  }, [standingCharacters, values.gender]);

  // Re-run validation when the language changes so displayed error
  // messages are regenerated in the newly selected language.
  useEffect(() => {
    validateForm();
  }, [currentLanguage, validateForm]);

  useEffect(() => {
    if (!values.profile_image || !values.gender) return;

    const selectedStandingCharacter = standingCharacters.find((item) => item.image === values.profile_image);
    if (!selectedStandingCharacter) return;

    if (selectedStandingCharacter.gender !== values.gender) {
      setFieldValue("profile_image", null);
    }
  }, [values.gender, values.profile_image, standingCharacters, setFieldValue]);

  useEffect(() => {
    if (!phoneUpdateVerification) return;
    if (
      phoneUpdateVerification.phoneNumber === values.phone_number &&
      (phoneUpdateVerification.countryCode || "") === (values.country_code || "")
    ) {
      setVerifiedPhone({
        phoneNumber: phoneUpdateVerification.phoneNumber,
        countryCode: phoneUpdateVerification.countryCode,
      });
    }
    dispatch(clearPhoneUpdateVerificationData());
  }, [phoneUpdateVerification?.verifiedAt]);

  // Custom handler for DOB to enforce 13+ age and prevent invalid dates (e.g., on iOS)
  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;
    if (selectedDate) {
      const selected = new Date(selectedDate);
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() - 13);
      maxDate.setHours(23, 59, 59, 999);

      if (selected > maxDate) {
        // Reset to max allowed date if a too-recent date (younger than 13) is selected
        setFieldValue("dob", maxDate.toISOString().split("T")[0]);
        return;
      }
    }
    setFieldValue("dob", selectedDate);
  };

  // Handler for Calendar date selection
  const handleCalendarDateSelect = (date: Date) => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 13);
    maxDate.setHours(23, 59, 59, 999);

    if (date > maxDate) {
      // Don't allow dates that would make user younger than 13
      return;
    }

    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    setFieldValue("dob", formattedDate);
    setIsDobPickerOpen(false);
  };

  // Format DOB for display
  const formatDobForDisplay = (dob: string) => {
    if (!dob) return "";
    // Parse date components to avoid timezone issues
    const [year, month, day] = dob.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(currentLanguage === "ar" ? "ar-SA" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Calculate dropdown position and handle click outside
  useEffect(() => {
    if (isCountryDropdownOpen && countryButtonRef.current) {
      const buttonRect = countryButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
        left: buttonRect.left,
        width: buttonRect.width,
      });
      // Mark position as ready after calculation
      setIsCountryPositionReady(true);
    } else {
      // Reset position ready state when dropdown is closed
      setIsCountryPositionReady(false);
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".country-dropdown-container") &&
        !target.closest(".country-dropdown-portal")
      ) {
        setIsCountryDropdownOpen(false);
        setCountrySearchQuery("");
      }
    };

    if (isCountryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Recalculate position on scroll/resize
      const handleResize = () => {
        if (countryButtonRef.current) {
          const buttonRect = countryButtonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
            left: buttonRect.left,
            width: buttonRect.width,
          });
        }
      };
      window.addEventListener("scroll", handleResize, true);
      window.addEventListener("resize", handleResize);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleResize, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isCountryDropdownOpen]);

  // Calculate nationality dropdown position and handle click outside
  useEffect(() => {
    if (isNationalityDropdownOpen && nationalityButtonRef.current) {
      const buttonRect = nationalityButtonRef.current.getBoundingClientRect();
      setNationalityDropdownPosition({
        top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
        left: buttonRect.left,
        width: buttonRect.width,
      });
      // Mark position as ready after calculation
      setIsNationalityPositionReady(true);
    } else {
      // Reset position ready state when dropdown is closed
      setIsNationalityPositionReady(false);
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".nationality-dropdown-container") &&
        !target.closest(".nationality-dropdown-portal")
      ) {
        setIsNationalityDropdownOpen(false);
        setNationalitySearchQuery("");
      }
    };

    if (isNationalityDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Recalculate position on scroll/resize
      const handleResize = () => {
        if (nationalityButtonRef.current) {
          const buttonRect = nationalityButtonRef.current.getBoundingClientRect();
          setNationalityDropdownPosition({
            top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
            left: buttonRect.left,
            width: buttonRect.width,
          });
        }
      };
      window.addEventListener("scroll", handleResize, true);
      window.addEventListener("resize", handleResize);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleResize, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isNationalityDropdownOpen]);

  // Calculate DOB picker dropdown position and handle click outside
  useEffect(() => {
    if (isDobPickerOpen && dobButtonRef.current) {
      const buttonRect = dobButtonRef.current.getBoundingClientRect();
      setDobDropdownPosition({
        top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
        left: buttonRect.left,
        width: buttonRect.width,
      });
      // Mark position as ready after calculation
      setIsDobPositionReady(true);
    } else {
      // Reset position ready state when picker is closed
      setIsDobPositionReady(false);
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".dob-picker-container") &&
        !target.closest(".dob-picker-portal")
      ) {
        setIsDobPickerOpen(false);
      }
    };

    if (isDobPickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Recalculate position on scroll/resize
      const handleResize = () => {
        if (dobButtonRef.current) {
          const buttonRect = dobButtonRef.current.getBoundingClientRect();
          setDobDropdownPosition({
            top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
            left: buttonRect.left,
            width: buttonRect.width,
          });
        }
      };
      window.addEventListener("scroll", handleResize, true);
      window.addEventListener("resize", handleResize);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleResize, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isDobPickerOpen]);

  // Filter countries based on search query
  const filteredCountries = countryList.filter((country: { id: string; country_name: string; country_code: string }) => {
    const searchLower = countrySearchQuery.toLowerCase();
    return (
      country.country_name.toLowerCase().includes(searchLower) ||
      country.country_code.includes(countrySearchQuery)
    );
  });

  const handleCountrySelect = (countryCode: string) => {
    const truncatedPhone = sanitizePhoneInput(values.phone_number || "", countryCode);
    setFieldValue("country_code", countryCode);
    setFieldValue("phone_number", truncatedPhone, true);
    setIsCountryDropdownOpen(false);
    setCountrySearchQuery("");
  };

  // Filter nationalities based on search query
  const filteredNationalities = countryList.filter((country: { id: string; country_name: string; country_name_full: any }) => {
    const searchLower = nationalitySearchQuery.toLowerCase();
    return (
      country.country_name.toLowerCase().includes(searchLower) ||
      country.country_name_full?.en?.toLowerCase().includes(searchLower) ||
      country.country_name_full?.ar?.toLowerCase().includes(searchLower)
    );
  });

  const handleNationalitySelect = (countryNameFull: any) => {
    setFieldValue("address", JSON.stringify(countryNameFull));
    setIsNationalityDropdownOpen(false);
    setNationalitySearchQuery("");
  };

  // Get selected nationality display text
  const getSelectedNationality = () => {
    if (!values.address) return "";
    try {
      const parsed = JSON.parse(values.address);
      return parsed[currentLanguage as keyof typeof parsed] || "";
    } catch {
      return values.address;
    }
  };

  // Get selected nationality flag
  const getSelectedNationalityFlag = () => {
    if (!values.address) return null;
    try {
      const parsed = JSON.parse(values.address);
      const selectedCountry = countryList.find(
        (country: { country_name_full: any }) =>
          JSON.stringify(country.country_name_full) === values.address
      );
      return selectedCountry?.flag_svg || null;
    } catch {
      return null;
    }
  };

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: updateUserProfileAPI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      showToast(t("messages.profileUpdated"), "success");
      dispatch(setUser(user as any));
      navigate("/create-game");
    },
    onError: (error: any) => {
      showToast(error.message || t("messages.profileUpdateFailed"), "error");
    },
  });

  // Refetch latest user data when My Account gains focus (and on mount)
  useEffect(() => {
    const handleFocus = () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    };

    // Fetch immediately on mount
    handleFocus();

    // Refetch whenever window gains focus
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [queryClient]);

  const existingPhoneNumber = user?.phone_number || "";
  const existingCountryCode = user?.country_code || "973";
  const currentPhoneNumber = values.phone_number || "";
  const currentCountryCode = values.country_code || "973";
  const hasPhoneChanged = currentPhoneNumber !== existingPhoneNumber || currentCountryCode !== existingCountryCode;
  const needsPhoneVerification = hasPhoneChanged && !!currentPhoneNumber;
  const isCurrentPhoneVerified =
    !needsPhoneVerification ||
    (verifiedPhone?.phoneNumber === currentPhoneNumber && (verifiedPhone?.countryCode || "") === currentCountryCode);

  const isSaveProfileDisabled = isPending || (needsPhoneVerification && !isCurrentPhoneVerified);
  const isVerifyButtonDisabled = isSendingPhoneOtp || isCountryListLoading || !needsPhoneVerification || isCurrentPhoneVerified;
  const verifyButtonText = isCurrentPhoneVerified
    ? t("myAccount.verified")
    : isSendingPhoneOtp
      ? t("myAccount.sendingOtp")
      : t("myAccount.verify");

  const handleVerifyPhoneNumber = async () => {
    if (isVerifyButtonDisabled) return;

    if (!currentPhoneNumber || !REGEX.NUMBERS_ONLY.test(currentPhoneNumber) || !isValidPhoneLength(currentPhoneNumber, currentCountryCode)) {
      setFieldError(
        "phone_number",
        !currentPhoneNumber
          ? t("validation.mobileRequired")
          : !REGEX.NUMBERS_ONLY.test(currentPhoneNumber)
            ? t("validation.mobileValid")
            : t(`validation.${getPhoneLengthErrorKey(currentCountryCode)}`)
      );
      setFieldTouched("phone_number", true, false);
      return;
    }

    setIsSendingPhoneOtp(true);
    try {
      await checkPhoneExistsUpdateAPI({
        phone_number: currentPhoneNumber,
        country_code: currentCountryCode,
      });

      if (!recaptchaContainerRef.current) {
        throw new Error("Recaptcha container element is required");
      }

      const fullPhoneNumber = `+${currentCountryCode}${currentPhoneNumber}`;
      await sendPhoneOtp(fullPhoneNumber, recaptchaContainerRef.current);
      showToast(t("messages.otpSentSuccess"), "success");

      dispatch(
        setOtpVerificationData({
          phoneNumber: currentPhoneNumber,
          countryCode: currentCountryCode,
          fullName: "",
          isNewUser: false,
          flowType: "updateMobile",
        })
      );
      dispatch(openModal("otpVerify"));
    } catch (error: any) {
      const message = error instanceof Error ? error.message : "";
      if (isFirebaseInvalidPhoneError(error)) {
        setFieldError("phone_number", t("validation.mobileValid"));
        setFieldTouched("phone_number", true, false);
      } else if (/already exists|already registered|مستخدم|موجود/i.test(message)) {
        setFieldError("phone_number", message || t("validation.mobileValid"));
        setFieldTouched("phone_number", true, false);
      } else {
        showToast(getSendPhoneOtpToastMessage(error, t), "error");
      }
    } finally {
      setIsSendingPhoneOtp(false);
    }
  };

  const existingEmail = (user?.email || "").trim().toLowerCase();
  const currentEmail = (values.email || "").trim().toLowerCase();
  const hasEmailChanged = !!currentEmail && currentEmail !== existingEmail;
  const isSocialEmailLocked =
    user?.social_type === "google" ||
    user?.social_type === "facebook" ||
    user?.social_type === "apple";
  const isCurrentEmailVerified =
    !!currentEmail && !hasEmailChanged && user?.is_email_verified !== false;
  const canSendEmailVerification =
    !isSocialEmailLocked &&
    !!currentEmail &&
    REGEX.EMAIL.test(currentEmail) &&
    !isSendingEmailVerification &&
    (hasEmailChanged || user?.is_email_verified === false);

  const handleSendEmailVerification = async () => {
    if (!canSendEmailVerification) return;

    try {
      setIsSendingEmailVerification(true);
      await sendEmailUpdateVerificationAPI(currentEmail);
      dispatch(setEmailVerification(currentEmail));
      dispatch(openModal("verifyEmail"));
      showToast(t("verifyEmail.emailResentSuccess"), "success");
    } catch (error: any) {
      const message = error instanceof Error ? error.message : t("verifyEmail.failedToResend");
      if (/already exists|already registered|مستخدم|موجود/i.test(message)) {
        setFieldError("email", message);
        setFieldTouched("email", true, false);
      } else {
        showToast(message, "error");
      }
    } finally {
      setIsSendingEmailVerification(false);
    }
  };


  const isRenderChangePassword = user?.register_from === "email";
  // const isRenderChangePassword = false;

  return (
    <>
      <div className="wrapper">
        {/* Invisible reCAPTCHA container for Firebase Phone Auth */}
        <div ref={recaptchaContainerRef} id="recaptcha-container-myaccount" className="hidden" aria-hidden="true" />
        <CmsBanner
          title={t("myAccount.title")}
          description={t("myAccount.description")}
        />
        <section className="2xl:py-[3.5vw] lg:py-15 py-10 relative flex-1 bg-primary-ivory">
          <div className="w-full mx-auto px-[4vmin] relative z-20">
            <div className="2xl:max-w-[67.71vw] max-w-[1040px] mx-auto">
              <div className="2xl:border-[0.13vw] border-2 border-black-300 shadow-[0px_4px_0px_0px_#110706] 2xl:rounded-[1.56vw] lg:rounded-3xl rounded-2xl bg-white ">
                <div className="flex flex-col 2xl:gap-[1vw] md:gap-5 sm:gap-4 gap-4">
                  <div className="w-full 2xl:border-[0.13vw] border-2 border-black-300 bg-primary-orange 2xl:rounded-[1.56vw] lg:rounded-3xl rounded-2xl flex items-center">

                    <div className="2xl:gap-[0.85vw] gap-3 w-full h-full 2xl:py-[2.11vw] py-4 2xl:px-[1.9vw] px-3.5 relative flex">
                      {
                        (
                          <div className="absolute landscape:max-lg:top-[9px] top-3 md:top-[14px] lg:top-[12px] right-0 z-10 2xl:top-[14px]">
                            <span className="2xl:text-[1.17vw] text-md font-bold text-black-300 text-center leading-none  bg-primary-skyblue rounded-xl landscape:max-lg:rounded-tr-2xl rounded-tr-3xl p-4">
                              {user?.display_category_name && ` ${user?.display_category_name?.[currentLanguage]} `}
                              {user?.display_achievement_name && `${user?.display_achievement_name?.[currentLanguage]} | `}
                              {user?.IQ != null && (
                                <span className="2xl:text-[1.17vw] text-md font-bold text-black-300">
                                  IQ: {user.IQ}
                                </span>
                              )}
                            </span>
                          </div>
                        )
                      }
                      <div className="w-[40%] 2xl:min-h-[300px] sm:min-h-[260px] relative bg-primary-yellow rounded-2xl border-2 border-black-300">
                        <div className="2xl:w-[15.42vw] md:w-[250px] sm:w-[150px] w-[200px] overflow-hidden aspect-square  lg:border-[0.4vw] border-5 border-transparent absolute left-1/2 top-1/2 -translate-1/2 flex justify-center items-center">
                          {values.profile_image ? (
                            <img src={values.profile_image} alt="Profile character" className="w-full h-full object-contain" />
                          ) : (
                            <img
                              src={ProfileIcon}
                              alt=""
                              className="2xl:max-w-[5.20vw] md:max-w-[102px] sm:max-w-[50px] max-w-[60px] w-full"
                            />
                          )}
                        </div>

                      </div>
                      <div className="w-[60%] 2xl:px-[1.20vw] px-4 py-2 bg-primary-yellow rounded-2xl border-2 border-black-300 flex flex-col justify-center items-center">
                        <div className="flex gap-4 justify-center items-center min-w-full 2xl:py-[0.5vw] py-2">
                          <label
                            htmlFor="gender-male"
                            className={`flex items-center 2xl:gap-[0.4vw] gap-2 lg:text-[1.1vw]/[1.4vw] md:text-base/tight text-sm/tight font-medium cursor-pointer transition-all 2xl:px-[0.8vw] px-3 2xl:py-[0.4vw] py-1.5 bg-primary-yellow 2xl:border-[0.11vw] border-2 border-black-300 2xl:rounded-[0.44vw] rounded-lg 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] ${values.gender === "male" ? "border-[#B35840] ring-2 ring-[#B35840]" : ""}`}
                          >
                            <input
                              id="gender-male"
                              onChange={handleChange}
                              name="gender"
                              type="radio"
                              value="male"
                              checked={values.gender === "male"}
                              className="hidden"
                            />
                            <span className={`2xl:w-[1vw] 2xl:h-[1vw] w-4 h-4 rounded-full border-2 flex items-center justify-center ${values.gender === "male" ? "border-[#B35840]" : "border-black-400"}`}>
                              {values.gender === "male" && <span className="2xl:w-[0.5vw] 2xl:h-[0.5vw] w-2 h-2 rounded-full bg-[#B35840]" />}
                            </span>
                            <span className="font-bold leading-normal text-black-300">{t("myAccount.male")}</span>
                          </label>
                          <label
                            htmlFor="gender-female"
                            className={`flex items-center 2xl:gap-[0.4vw] gap-2 lg:text-[1.1vw]/[1.4vw] md:text-base/tight text-sm/tight font-medium cursor-pointer transition-all 2xl:px-[0.8vw] px-3 2xl:py-[0.4vw] py-1.5 bg-primary-yellow 2xl:border-[0.11vw] border-2 border-black-300 2xl:rounded-[0.44vw] rounded-lg 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] ${values.gender === "female" ? "border-[#B35840] ring-2 ring-[#B35840]" : ""}`}
                          >
                            <input
                              id="gender-female"
                              onChange={handleChange}
                              name="gender"
                              type="radio"
                              value="female"
                              checked={values.gender === "female"}
                              className="hidden"
                            />
                            <span className={`2xl:w-[1vw] 2xl:h-[1vw] w-4 h-4 rounded-full border-2 flex items-center justify-center ${values.gender === "female" ? "border-[#B35840]" : "border-black-400"}`}>
                              {values.gender === "female" && <span className="2xl:w-[0.5vw] 2xl:h-[0.5vw] w-2 h-2 rounded-full bg-[#B35840]" />}
                            </span>
                            <span className="font-bold leading-normal text-black-300">{t("myAccount.female")}</span>
                          </label>
                        </div>
                        {errors.gender && touched.gender && (
                          <p className="text-red-500 lg:text-[1vw] text-sm 2xl:mt-[0.3vw] mt-1 text-center font-bold">
                            {errors?.gender as string}
                          </p>
                        )}
                        <div className="2xl:mt-[0.8vw] mt-2">
                          <div className="flex flex-wrap justify-center gap-2">
                            {filteredStandingCharacters.map((character: CharacterItem) => (
                              <button
                                key={character.id}
                                type="button"
                                onClick={() => setFieldValue("profile_image", character.image)}
                                className={`relative 2xl:w-[10vw] 2xl:h-[10vw] xl:w-[100px] xl:h-[100px] lg:w-24 lg:h-24 md:w-24 md:h-24 w-24 h-24 bg-white rounded-lg border-2 p-1 transition-all ${values.profile_image === character.image
                                  ? "border-[#B35840] bg-[#FFE7DF]"
                                  : "border-black-300 hover:border-[#B35840]"
                                  }`}
                              >
                                <img src={character.image} alt="Character option" className="w-full h-full object-contain" />
                                {values.profile_image === character.image && (
                                  <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#B35840] text-white text-xs font-bold flex items-center justify-center">
                                    ✓
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full pt-2 px-3">
                    <div className="2xl:w-[27.67vw] lg:w-[400px] w-full mx-auto">

                      <div className="">
                        <label htmlFor="name" className={labelClasses}>
                          {t("myAccount.fullName")}
                          <span className="text-red-500 lg:text-[1.1vw] text-base ms-1">*</span>
                        </label>
                        <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                          <input
                            id="name"
                            onChange={(e) => {
                              const capitalizedValue = capitalizeWords(e.target.value);
                              setFieldValue("full_name", capitalizedValue);
                            }}
                            name="full_name"
                            type="text"
                            placeholder={t("myAccount.enterName")}
                            value={values.full_name}
                            className={`${inputClasses} flex-1`}
                          />
                        </div>
                        {errors.full_name && touched.full_name && (
                          <p className="text-red-500 lg:text-[1vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                            {errors?.full_name as string}
                          </p>
                        )}
                      </div>

                      <div className="">
                        <label htmlFor="dob" className={labelClasses}>
                          {t("myAccount.dateOfBirth")}
                          <span className="text-red-500 lg:text-[1.1vw] text-base ms-1">*</span>
                        </label>
                        <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                          <div className="dob-picker-container relative w-full">
                            <FiCalendar className="absolute rtl:right-0 rtl:left-auto left-0 top-1/2 -translate-y-1/2 text-[#110716] lg:text-[1.2vw] text-lg pointer-events-none z-10" />
                            <button
                              ref={dobButtonRef}
                              type="button"
                              onClick={() => setIsDobPickerOpen(!isDobPickerOpen)}
                              className={`${inputClasses} flex-1 lg:ps-[8%] ps-10 cursor-pointer text-left rtl:text-right w-full ${!values.dob ? "text-black-400" : ""}`}
                            >
                              {values.dob ? formatDobForDisplay(values.dob) : t("myAccount.enterDob")}
                            </button>
                            {/* Date Picker Portal */}
                            {isDobPickerOpen &&
                              isDobPositionReady &&
                              typeof document !== "undefined" &&
                              createPortal(
                                <div
                                  className="dob-picker-portal fixed z-[9999] bg-white border-2 border-black-300 2xl:rounded-[0.78vw] rounded-xl 2xl:shadow-[0px_0.26vw_0px_#110706] shadow-[0px_4px_0px_#110706] overflow-hidden"
                                  style={{
                                    top: `${dobDropdownPosition.top}px`,
                                    left: `${dobDropdownPosition.left}px`,
                                  }}
                                >
                                  <style>{`
                                    .rdrCalendarWrapper {
                                      background: white;
                                      font-family: inherit;
                                    }
                                    .rdrMonth {
                                      width: 100%;
                                    }
                                    .rdrMonthAndYearWrapper {
                                      padding-top: 0.5rem;
                                      padding-bottom: 0.5rem;
                                    }
                                    .rdrMonthAndYearPickers select {
                                      font-weight: 700;
                                      color: #110706;
                                      font-size: 0.95rem;
                                    }
                                    .rdrMonthPicker, .rdrYearPicker {
                                      margin: 0 0.25rem;
                                    }
                                    .rdrDay {
                                      font-weight: 600;
                                    }
                                    .rdrDayNumber span {
                                      color: #110706;
                                    }
                                    .rdrDayToday .rdrDayNumber span:after {
                                      background: #FF9865;
                                    }
                                    .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
                                    .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
                                    .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
                                    .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
                                      color: white;
                                    }
                                    .rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview {
                                      background: rgba(255, 152, 101, 0.2);
                                      border-color: #FF9865;
                                    }
                                    .rdrDayStartOfMonth .rdrInRange, .rdrDayStartOfMonth .rdrEndEdge, .rdrDayStartOfWeek .rdrInRange, .rdrDayStartOfWeek .rdrEndEdge {
                                      border-top-left-radius: 0.5rem;
                                      border-bottom-left-radius: 0.5rem;
                                    }
                                    .rdrDayEndOfMonth .rdrInRange, .rdrDayEndOfMonth .rdrStartEdge, .rdrDayEndOfWeek .rdrInRange, .rdrDayEndOfWeek .rdrStartEdge {
                                      border-top-right-radius: 0.5rem;
                                      border-bottom-right-radius: 0.5rem;
                                    }
                                    .rdrStartEdge, .rdrEndEdge, .rdrInRange {
                                      background: #FF9865;
                                    }
                                    .rdrDayHovered .rdrDayNumber:after {
                                      border-color: #FF9865;
                                    }
                                    .rdrNextPrevButton {
                                      background: #FFFBA1;
                                      border: 2px solid #110706;
                                      border-radius: 0.375rem;
                                    }
                                    .rdrNextPrevButton:hover {
                                      background: #FFE66D;
                                    }
                                    .rdrPprevButton i {
                                      border-color: transparent #110706 transparent transparent;
                                    }
                                    .rdrNextButton i {
                                      border-color: transparent transparent transparent #110706;
                                    }
                                    [dir="rtl"] .rdrPprevButton i {
                                      margin: auto;
                                      border-width: 4px 4px 4px 6px;
                                      border-color: transparent transparent transparent #110706;
                                      transform: translate(3px, 0px);
                                    }
                                    [dir="rtl"] .rdrNextButton i {
                                      margin: auto;
                                      border-width: 4px 6px 4px 4px;
                                      border-color: transparent #110706 transparent transparent;
                                      transform: translate(-3px, 0px);
                                    }
                                    .rdrWeekDay {
                                      font-weight: 700;
                                      color: #110706;
                                    }
                                    .rdrMonthName {
                                      font-weight: 700;
                                      color: #110706;
                                    }
                                    @media (max-width: 768px) {
                                      .rdrCalendarWrapper {
                                        font-size: 14px;
                                      }
                                      .rdrMonth {
                                        width: 280px;
                                      }
                                    }
                                  `}</style>
                                  <Calendar
                                    date={values.dob ? (() => {
                                      const [year, month, day] = values.dob.split('-').map(Number);
                                      return new Date(year, month - 1, day);
                                    })() : maxDobDate}
                                    onChange={handleCalendarDateSelect}
                                    maxDate={maxDobDate}
                                    color="#FF9865"
                                    rangeColors={["#FF9865"]}
                                    showMonthAndYearPickers={true}
                                  />
                                </div>,
                                document.body
                              )}
                          </div>
                        </div>
                        {errors.dob && touched.dob && (
                          <p className="text-red-500 lg:text-[1vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                            {errors?.dob as string}
                          </p>
                        )}
                      </div>

                      <div className="">
                        <label htmlFor="address" className={labelClasses}>
                          {t("myAccount.nationality")}
                          <span className="text-red-500 lg:text-[1.1vw] text-base ms-1">*</span>
                        </label>
                        <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                          <div className="nationality-dropdown-container relative w-full">
                            {getSelectedNationalityFlag() ? (
                              <img
                                src={getSelectedNationalityFlag()!}
                                alt="Selected country flag"
                                className="absolute rtl:right-0 rtl:left-auto left-0 top-1/2 -translate-y-1/2 2xl:w-[1.4vw] 2xl:h-[1.05vw] w-6 h-5 object-cover rounded-sm pointer-events-none z-10"
                              />
                            ) : (
                              <HiOutlineGlobeAlt className="absolute rtl:right-0 rtl:left-auto left-0 top-1/2 -translate-y-1/2 text-[#110716] lg:text-[1.2vw] text-lg pointer-events-none z-10" />
                            )}
                            <button
                              ref={nationalityButtonRef}
                              type="button"
                              onClick={() => {
                                if (!isCountryListLoading) {
                                  setIsNationalityDropdownOpen(!isNationalityDropdownOpen);
                                  if (!isNationalityDropdownOpen) {
                                    setNationalitySearchQuery("");
                                  }
                                }
                              }}
                              disabled={isCountryListLoading}
                              className={`${inputClasses} flex-1 lg:ps-[8%] ps-10 pe-10 cursor-pointer text-left rtl:text-right w-full ${!getSelectedNationality() ? "text-black-400" : ""}`}
                            >
                              <span className="ml-0 md:ml-1">
                                {getSelectedNationality() || t("myAccount.selectNationality")}
                              </span>
                            </button>
                            {/* Dropdown Arrow */}
                            <div className="absolute rtl:right-auto rtl:left-0 right-0 inset-y-0 end-4 flex items-center pointer-events-none">
                              <svg
                                className={`lg:w-[0.8vw] w-3 lg:h-[0.8vw] h-3 text-[#110716] transition-transform ${isNationalityDropdownOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>

                            {/* Dropdown Menu - Rendered via Portal */}
                            {isNationalityDropdownOpen &&
                              isNationalityPositionReady &&
                              typeof document !== "undefined" &&
                              createPortal(
                                <div
                                  className="nationality-dropdown-portal fixed z-[9999] lg:w-[25vw] w-[320px] 2xl:max-h-[20vw] max-h-[350px] bg-primary-ivory border border-black-300 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] overflow-hidden"
                                  style={{
                                    top: `${nationalityDropdownPosition.top}px`,
                                    left: `${nationalityDropdownPosition.left}px`,
                                    width: `${nationalityDropdownPosition.width}px`,
                                  }}
                                >
                                  {/* Search Input */}
                                  <div className="2xl:p-[0.5vw] p-2 border-b border-black-300">
                                    <input
                                      type="text"
                                      placeholder={t("myAccount.searchCountry")}
                                      value={nationalitySearchQuery}
                                      onChange={(e) => setNationalitySearchQuery(e.target.value)}
                                      className="w-full lg:py-[0.4vw] py-1.5 lg:px-[0.6vw] px-2 2xl:rounded-[0.26vw] rounded-[5px] lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight font-bold border border-black-300 text-black-300 focus:outline-none focus:border-primary-orange transition-all"
                                      autoFocus
                                    />
                                  </div>

                                  {/* Country List */}
                                  <div className="overflow-y-auto 2xl:max-h-[calc(20vw-4vw)] max-h-[290px]">
                                    {filteredNationalities.length > 0 ? (
                                      filteredNationalities.map((country: { id: string; country_name: string; country_name_full: any; flag_svg?: string }) => (
                                        <button
                                          key={country.id}
                                          type="button"
                                          onClick={() => handleNationalitySelect(country.country_name_full)}
                                          className={`w-full text-start lg:py-[0.5vw] py-2 lg:px-[0.8vw] px-3 lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight font-bold hover:bg-primary-yellow transition-colors flex items-center gap-2 ${values.address === JSON.stringify(country.country_name_full) ? "bg-primary-yellow" : ""
                                            }`}
                                        >
                                          {country.flag_svg && (
                                            <img
                                              src={country.flag_svg}
                                              alt={`${country.country_name} flag`}
                                              className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm shrink-0"
                                            />
                                          )}
                                          <span className="flex-1 truncate">
                                            {country.country_name_full[currentLanguage as keyof typeof country.country_name_full]}
                                          </span>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="lg:py-[0.8vw] py-3 lg:px-[0.8vw] px-3 lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight text-black-400 text-center">
                                        {t("login.noCountryFound") || "No country found"}
                                      </div>
                                    )}
                                  </div>
                                </div>,
                                document.body
                              )}
                          </div>
                        </div>
                        {errors.address && touched.address && (
                          <p className="text-red-500 lg:text-[1vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                            {errors?.address as string}
                          </p>
                        )}
                      </div>

                      <div className="">
                        <label htmlFor="phone" className={labelClasses}>
                          {t("myAccount.phoneNumber")}
                        </label>
                        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all ${errors.phone_number && touched.phone_number ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]" : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"}`}>
                          {/* Phone input with country code dropdown inside */}
                          <div className="relative w-full">
                            {/* Phone Number Input */}
                            <input
                              id="phone"
                              onChange={(e) => {
                                setFieldValue(
                                  "phone_number",
                                  sanitizePhoneInput(e.target.value, values.country_code)
                                );
                              }}
                              onBlur={handleBlur}
                              maxLength={getPhoneMaxLength(values.country_code)}
                              name="phone_number"
                              type="tel"
                              placeholder={t("myAccount.enterPhone")}
                              value={values.phone_number}
                              className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:ps-[7.6vw] md:ps-[100px] ps-[100px] rtl:lg:pe-[7.6vw] rtl:md:pe-[100px] rtl:pe-[100px] lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 placeholder:text-[15px] focus:outline-none transition-all disabled:opacity-50 bg-transparent ${errors.phone_number && touched.phone_number ? "!border-red-500" : ""
                                }`}
                            />

                            {/* Country Code Dropdown positioned inside input */}
                            <div className="absolute inset-y-0 start-0 flex items-center">
                              <div className="country-dropdown-container relative lg:min-w-[6vw] min-w-[90px]">
                                <button
                                  ref={countryButtonRef}
                                  type="button"
                                  onClick={() => {
                                    if (!isCountryListLoading) {
                                      setIsCountryDropdownOpen(!isCountryDropdownOpen);
                                      if (!isCountryDropdownOpen) {
                                        setCountrySearchQuery("");
                                      }
                                    }
                                  }}
                                  disabled={isCountryListLoading}
                                  className={`rtl:text-right w-full h-full lg:py-[0.55vw] md:py-2.5 py-2 lg:px-[0.8vw] px-2 pe-7 2xl:rounded-[0.26vw] rounded-[5px] lg:text-[0.95vw]/[1.2vw] md:text-sm/tight text-xs/tight font-bold bg-primary-ivory hover:bg-primary-yellow focus:bg-primary-yellow border border-black-300 text-black-300 focus:outline-none transition-all disabled:opacity-50 cursor-pointer flex items-center justify-between ${errors.country_code && touched.country_code ? "!border-red-500" : ""
                                    }`}
                                >
                                  <span className="truncate">+{values.country_code || ""}</span>
                                  <svg
                                    className={`lg:w-[0.8vw] w-3 lg:h-[0.8vw] h-3 text-[#110716] transition-transform ${isCountryDropdownOpen ? "rotate-180" : ""}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>

                                {/* Dropdown Menu - Rendered via Portal */}
                                {isCountryDropdownOpen &&
                                  isCountryPositionReady &&
                                  typeof document !== "undefined" &&
                                  createPortal(
                                    <div
                                      className="country-dropdown-portal fixed z-[9999] lg:w-[20vw] w-[280px] 2xl:max-h-[20vw] max-h-[300px] bg-primary-ivory border border-black-300 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] overflow-hidden"
                                      style={{
                                        top: `${dropdownPosition.top}px`,
                                        left: `${dropdownPosition.left}px`,
                                      }}
                                    >
                                      {/* Search Input */}
                                      <div className="2xl:p-[0.5vw] p-2 border-b border-black-300">
                                        <input
                                          type="text"
                                          placeholder={t("login.searchCountry") || "Search country..."}
                                          value={countrySearchQuery}
                                          onChange={(e) => setCountrySearchQuery(e.target.value)}
                                          className="w-full lg:py-[0.4vw] py-1.5 lg:px-[0.6vw] px-2 2xl:rounded-[0.26vw] rounded-[5px] lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight font-bold border border-black-300 text-black-300 focus:outline-none focus:border-primary-orange transition-all"
                                          autoFocus
                                        />
                                      </div>

                                      {/* Country List */}
                                      <div className="overflow-y-auto 2xl:max-h-[calc(20vw-4vw)] max-h-[240px]">
                                        {filteredCountries.length > 0 ? (
                                          filteredCountries.map((country: { id: string; country_name: string; country_code: string; flag_svg?: string }) => (
                                            <button
                                              key={country.id}
                                              type="button"
                                              onClick={() => handleCountrySelect(country.country_code)}
                                              className={`w-full text-start lg:py-[0.5vw] py-2 lg:px-[0.8vw] px-3 lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight font-bold hover:bg-primary-yellow transition-colors flex items-center gap-2 ${values.country_code === country.country_code ? "bg-primary-yellow" : ""
                                                }`}
                                            >
                                              {country.flag_svg && (
                                                <img
                                                  src={country.flag_svg}
                                                  alt={`${country.country_name} flag`}
                                                  className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm flex-shrink-0"
                                                />
                                              )}
                                              <span className="flex-1 truncate">{country.country_name}</span>
                                              <span className="lg:ms-[0.5vw] ms-2 text-black-400">+{country.country_code}</span>
                                            </button>
                                          ))
                                        ) : (
                                          <div className="lg:py-[0.8vw] py-3 lg:px-[0.8vw] px-3 lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight text-black-400 text-center">
                                            {t("login.noCountryFound") || "No country found"}
                                          </div>
                                        )}
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                              </div>
                            </div>

                            {/* Verify (text) button inside phone field */}
                            {values.phone_number && values.phone_number?.length > 7 && <div className="absolute inset-y-0 end-0 flex items-center pe-3">
                              <button
                                type="button"
                                onClick={handleVerifyPhoneNumber}
                                disabled={isVerifyButtonDisabled}
                                className="lg:text-[1vw] font-bold text-primary-orange hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                              >
                                {verifyButtonText}
                              </button>
                            </div>}
                          </div>
                        </div>
                        {errors.country_code && touched.country_code && (
                          <p className="text-red-500 lg:text-[1vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                            {errors?.country_code as string}
                          </p>
                        )}
                        {errors.phone_number && touched.phone_number && (
                          <p className="text-red-500 lg:text-[1vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                            {errors?.phone_number as string}
                          </p>
                        )}
                        {needsPhoneVerification && !isCurrentPhoneVerified && values.phone_number && values.phone_number?.length > 7 && (
                          <p className="text-red-500 lg:text-[0.95vw] text-xs font-bold lg:mt-2 mt-2 lg:px-[5%] px-4">
                            {t("myAccount.verifyPhoneToSave")}
                          </p>
                        )}
                      </div>


                      <div className="">
                        <label htmlFor="email" className={labelClasses}>
                          {t("myAccount.emailAddress")}
                        </label>
                        <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                          <div className="relative w-full">
                            <FiMail className="absolute rtl:right-0 rtl:left-auto left-0 top-1/2 -translate-y-1/2 text-[#110716] lg:text-[1.2vw] text-lg pointer-events-none" />
                            <input
                              id="email"
                              disabled={isSocialEmailLocked}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              name="email"
                              type="text"
                              inputMode="email"
                              autoComplete="email"
                              placeholder={t("myAccount.enterEmail")}
                              value={values.email}
                              className={`${inputClasses} flex-1 lg:ps-[8%] ps-10 pe-24 ${isSocialEmailLocked
                                ? "bg-gray-200 text-gray-700"
                                : ""
                                }`}
                            />
                            {!!currentEmail && REGEX.EMAIL.test(currentEmail) && (
                              <div className="absolute inset-y-0 end-0 flex items-center pe-3">
                                {isCurrentEmailVerified ? (
                                  <span className="lg:text-[1vw] text-sm font-bold text-primary-orange">
                                    {t("myAccount.verified")}
                                  </span>
                                ) : !isSocialEmailLocked ? (
                                  <button
                                    type="button"
                                    onClick={handleSendEmailVerification}
                                    disabled={!canSendEmailVerification}
                                    className="lg:text-[1vw] font-bold text-primary-orange hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                                  >
                                    {isSendingEmailVerification
                                      ? t("verifyEmail.sendingLink")
                                      : t("verifyEmail.verify")}
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                        {errors.email && touched.email && (
                          <p className="text-red-500 lg:text-[1vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                            {errors?.email as string}
                          </p>
                        )}
                      </div>
                      <div className="2xl:mt-[2.52vh] md:mt-5 mt-4">
                        <BlueBtn
                          type="submit"
                          className="min-w-full disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleSubmit()}
                          disabled={isSaveProfileDisabled}
                        >
                          {isPending ? t("myAccount.saving") : t("myAccount.saveChanges")}
                        </BlueBtn>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="2xl:mt-[5.2vh] md:mt-12 mt-8 flex 2xl:gap-[1.25vw] gap-6 sm:flex-nowrap flex-wrap">

                {
                  isRenderChangePassword && (
                    <div className="sm:w-1/2 w-full">
                      <div className="2xl:py-[1.25vh] md:py-3 py-1 2xl:ps-[1vw] md:ps-5 ps-4 2xl:border-s-[0.20vw] border-s-4 border-primary">
                        <h4 className="font-garamond 2xl:text-[1.01vw] text-black md:text-xl/snug text-lg/snug font-bold uppercase">
                          {t("myAccount.changePassword")}
                        </h4>
                      </div>
                      <div className="2xl:ps-[1vw] md:ps-5 ps-4 2xl:mt-[1.67vh] md:mt-4 mt-3">
                        <p className=" text-[#282828] 2xl:text-[0.93vw] md:text-lg/tight text-base/tight">
                          {t("myAccount.changePasswordDescription")}
                        </p>
                        <MintGreenBtn
                          className="min-w-full 2xl:mt-[1.67vh] md:mt-4 mt-3"
                          onClick={() => setActiveModal("changePassword")}
                        >
                          {t("myAccount.changePasswordBtn")}
                        </MintGreenBtn>
                      </div>
                    </div>
                  )}

                {/* <div className={`${isRenderChangePassword ? "w-full sm:w-1/2" : "w-full"}`}>
                  <div className="2xl:py-[1.25vh] md:py-3 py-1 2xl:ps-[1vw] md:ps-5 ps-4 2xl:border-s-[0.20vw] border-s-4 border-[#EF5350]">
                    <h4 className="font-garamond 2xl:text-[1.01vw] text-black md:text-xl/snug text-lg/snug font-bold uppercase">
                      {t("myAccount.deleteAccount")}
                    </h4>
                  </div>
                  <div className="2xl:ps-[1vw] md:ps-5 ps-4 2xl:mt-[1.67vh] md:mt-4 mt-3">
                    <p className="text-[#282828] 2xl:text-[0.93vw] md:text-lg/tight text-base/tight mb-3">
                      {t("myAccount.deleteAccountDescription")}
                    </p>
                    <BlueBtn
                      className={`${isRenderChangePassword ? "min-w-full" : "w-1/2"} 2xl:mt-[1.67vh] md:mt-4 mt-3`}
                      onClick={() => setActiveModal("deleteAccount")}
                    >
                      {t("myAccount.deleteAccountBtn")}
                    </BlueBtn>
                  </div>
                </div> */}

              </div>
            </div>
          </div>
        </section >

        {/* Change password modal */}
        {
          activeModal === "changePassword" && (
            <ChangePasswordModal isOpen={activeModal === "changePassword"} onClose={() => setActiveModal(null)} />
          )
        }
      </div >
    </>
  );
}
